import { useNavigate } from '@tanstack/react-router'
import { useInstallation } from '@/installation/api'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { AlertTriangle, CheckCircle2, Wifi, WifiOff, Thermometer, Zap } from 'lucide-react'
import { StepFrame } from '@/components/installFlow/StepFrame'
import { StepFooter } from '@/components/installFlow/StepFooter'
import { useSelectedOrganizationId } from '@/organization/api'
import { useStepSearch } from '@/installFlow/useStepSearch'
import { useQuery } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { apiClient } from '@/api/client'
import { ApiPath } from '@/api/path'
import { TSMetric } from '@/query/query'
import { LineChartResponse, SeriesDatumName } from '@/components/charts/lineChartResponse'
import { ONE_SECOND } from '@/dateRanges/windowSizes'
import { LAST_1_MINUTE } from '@/dateRanges/dateRanges'
import { Skeleton } from '@/components/ui/skeleton'
import { StepHeader } from '@/components/installFlow/StepHeader'
import { PhaseTypeConfigurationTable } from '@/refrigerant/PhaseTypeConfigurationTable'
import { InstallationData } from '@/installation/installation'

// ─── Types ────────────────────────────────────────────────────────────────────

type TemperatureReading = {
  name: string
  value: number
  unit: string
}

type CheckStatus = 'ok' | 'warning' | 'error' | 'loading'

// Sentinel value thresholds — TC sends -1000 if open, +1000 if shorted
const TC_OPEN_THRESHOLD  = -500
const TC_SHORT_THRESHOLD =  500

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractLatestTemperatures(response: LineChartResponse): TemperatureReading[] {
  return response.series
    .map(series => {
      const latestDatum = response.datums[series.name]?.find(
        d => d.name === SeriesDatumName.LATEST
      )
      if (!latestDatum || latestDatum.datum === null) return null
      return {
        name: series.name,
        value: latestDatum.datum,
        unit: response.legend.unit || '°F',
      }
    })
    .filter((item): item is TemperatureReading => item !== null)
}

function extractLatestValue(response: LineChartResponse, seriesIndex = 0): number | undefined {
  const series = response.series?.[seriesIndex]
  if (!series) return undefined
  const datum = response.datums[series.name]?.find(d => d.name === SeriesDatumName.LATEST)
  if (datum?.datum !== undefined && datum.datum !== null) return datum.datum
  const lastPoint = series.data?.[series.data.length - 1]
  return lastPoint?.[1] ?? undefined
}

function getTCSentinelStatus(value: number): 'open' | 'shorted' | 'ok' {
  if (value < TC_OPEN_THRESHOLD)  return 'open'
  if (value > TC_SHORT_THRESHOLD) return 'shorted'
  return 'ok'
}

// ─── Design tokens ────────────────────────────────────────────────────────────

const ROW_STYLES = {
  ok:      'bg-emerald-700 border-emerald-700',
  warning: 'bg-amber-50 border-amber-300',
  error:   'bg-red-600 border-red-600',
  loading: 'bg-gray-50 border-gray-200',
} as const

const ICON_STYLES = {
  ok:      'bg-emerald-600 text-white',
  warning: 'bg-amber-100 text-amber-600',
  error:   'bg-red-500 text-white',
  loading: 'bg-gray-100 text-gray-400',
} as const

const LABEL_STYLES = {
  ok:      'text-white',
  warning: 'text-gray-900',
  error:   'text-white',
  loading: 'text-gray-500',
} as const

const VALUE_STYLES = {
  ok:      'text-white',
  warning: 'text-gray-900',
  error:   'text-white',
  loading: 'text-gray-400',
} as const

const UNIT_STYLES = {
  ok:      'text-emerald-200',
  warning: 'text-gray-400',
  error:   'text-red-200',
  loading: 'text-gray-300',
} as const

// ─── Status Pill ──────────────────────────────────────────────────────────────

function StatusPill({ status }: { status: CheckStatus }) {
  const cfg = {
    ok:      { label: 'OK',       cls: 'bg-emerald-500 text-white border border-emerald-400' },
    warning: { label: 'WARNING',  cls: 'bg-amber-500 text-white border border-amber-400' },
    error:   { label: 'CHECK',    cls: 'bg-red-500 text-white border border-red-400' },
    loading: { label: 'LOADING…', cls: 'bg-gray-200 text-gray-400 border border-gray-200' },
  }[status]

  return (
    <span className={`text-[10px] font-black tracking-widest uppercase px-2.5 py-1 rounded-full leading-none ${cfg.cls}`}>
      {cfg.label}
    </span>
  )
}

// ─── Check Row ────────────────────────────────────────────────────────────────

function CheckRow({
  icon,
  label,
  status,
  value,
  unit,
  fixMessage,
}: {
  icon: React.ReactNode
  label: string
  status: CheckStatus
  value?: string
  unit?: string
  fixMessage?: string
}) {
  return (
    <div className={`rounded-xl border-2 transition-colors ${ROW_STYLES[status]}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${ICON_STYLES[status]}`}>
          {icon}
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold leading-tight ${LABEL_STYLES[status]}`}>{label}</p>
          {status === 'error' && fixMessage && (
            <p className="text-xs text-red-200 font-medium mt-0.5 leading-snug">{fixMessage}</p>
          )}
          {status === 'warning' && fixMessage && (
            <p className="text-xs text-amber-700 font-medium mt-0.5 leading-snug">{fixMessage}</p>
          )}
        </div>

        <div className="flex-shrink-0 flex items-center gap-2">
          {value !== undefined && (
            <span className={`text-sm font-bold tabular-nums ${VALUE_STYLES[status]}`}>
              {value}
              {unit && <span className={`text-xs font-normal ml-0.5 ${UNIT_STYLES[status]}`}>{unit}</span>}
            </span>
          )}
          <StatusPill status={status} />
        </div>
      </div>
    </div>
  )
}

// ─── Sub Row (TC / Phase) ─────────────────────────────────────────────────────

function SubRow({
  label,
  value,
  unit,
  ok,
  fixMessage,
}: {
  label: string
  value: string | undefined
  unit?: string
  ok: boolean
  fixMessage?: string
}) {
  return (
    <div className={`rounded-lg border-2 transition-colors ${ok ? 'bg-emerald-700 border-emerald-700' : 'bg-red-600 border-red-600'}`}>
      <div className="flex items-center justify-between px-4 min-h-[44px]">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full flex-shrink-0 bg-white" />
          <span className="text-sm font-semibold text-white">{label}</span>
        </div>
        <span className="text-sm font-bold tabular-nums text-white">
          {value !== undefined
            ? <>{value}<span className={`text-xs font-normal ml-0.5 ${ok ? 'text-emerald-200' : 'text-red-200'}`}>{unit}</span></>
            : '—'
          }
        </span>
      </div>
      {!ok && fixMessage && (
        <p className="text-xs text-red-200 font-medium px-4 pb-3 -mt-1 leading-snug">{fixMessage}</p>
      )}
    </div>
  )
}

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">
      {children}
    </p>
  )
}

// ─── Cellular Check ───────────────────────────────────────────────────────────

function CellularCheck({ hasCellular, isLoading }: { hasCellular: boolean; isLoading: boolean }) {
  return (
    <div>
      <SectionLabel>Connectivity</SectionLabel>
      <CheckRow
        icon={hasCellular || isLoading ? <Wifi size={18} /> : <WifiOff size={18} />}
        label="Cellular signal"
        status={isLoading ? 'loading' : hasCellular ? 'ok' : 'error'}
        fixMessage="No signal — reposition or check antenna connection"
      />
    </div>
  )
}

// ─── Power Check ──────────────────────────────────────────────────────────────

type PhaseReading = { label: string; value: number | undefined }

function PowerCheck({
  totalPower,
  phases,
  isLoading,
  installation,
}: {
  totalPower: number | undefined
  phases: PhaseReading[]
  isLoading: boolean
  installation: InstallationData
}) {
  const missingPhaseType  = installation?.equipment_model?.phase_count == null
  const anyPhaseConnected = phases.some(p => p.value !== undefined)
  const anyPhaseError     = phases.some(p => p.value === undefined)

  const overallStatus: CheckStatus = isLoading
    ? 'loading'
    : missingPhaseType
    ? 'warning'
    : phases.length === 0
    ? 'loading'
    : !anyPhaseConnected
    ? 'error'
    : anyPhaseError
    ? 'warning'
    : totalPower === undefined ? 'error' 
    : totalPower <= 0 
    ? 'error' : 'ok'

  return (
    <div>
      <SectionLabel>Power</SectionLabel>

      {missingPhaseType ? (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3 space-y-3">
          <p className="text-sm font-semibold text-gray-900">Phase type required</p>
          <p className="text-xs text-amber-700 leading-snug">
            Select the power configuration so current clamps read correctly.
          </p>
          <PhaseTypeConfigurationTable assets={[installation]} />
        </div>
      ) : (
        <>
          <CheckRow
            icon={<Zap size={18} />}
            label="Current clamps"
            status={overallStatus}
            value={!isLoading && totalPower !== undefined ? totalPower.toFixed(0) : undefined}
            unit="W power draw"
            fixMessage={
              !anyPhaseConnected
                ? 'No current readings — check all clamp connections before closing up'
                : anyPhaseError
                ? 'One or more phases missing — verify all clamps are seated'
                : undefined
            }
          />

          <div className="mt-1.5 flex flex-col gap-1.5">
            {isLoading
              ? ['L1', 'L2', 'L3'].map(l => <Skeleton key={l} className="h-11 rounded-lg" />)
              : phases.map(phase => {
                  const ok = phase.value !== undefined
                  return (
                    <SubRow
                      key={phase.label}
                      label={phase.label}
                      value={ok ? phase.value!.toFixed(1) : undefined}
                      unit="A"
                      ok={ok}
                      fixMessage={`Check the current clamp connection for ${phase.label}`}
                    />
                  )
                })
            }
          </div>
        </>
      )}
    </div>
  )
}

// ─── Temperature Check ────────────────────────────────────────────────────────

function TemperatureCheck({
  allNames,
  temperatures,
  isLoading,
}: {
  allNames: string[]
  temperatures: TemperatureReading[]
  isLoading: boolean
}) {
  const total            = allNames.length
  const missingCount     = total - temperatures.length
  const sentinelBadCount = temperatures.filter(t => getTCSentinelStatus(t.value) !== 'ok').length
  const badCount         = missingCount + sentinelBadCount
  const allGood          = total > 0 && badCount === 0
  const noneGood         = temperatures.length === 0

  const overallStatus: CheckStatus = isLoading || total === 0
    ? 'loading'
    : allGood
    ? 'ok'
    : noneGood
    ? 'error'
    : 'warning'

  const summaryFixMessage = (() => {
    if (isLoading || total === 0) return undefined
    const parts: string[] = []
    if (missingCount > 0)     parts.push(`${missingCount} sensor${missingCount > 1 ? 's' : ''} not connected`)
    if (sentinelBadCount > 0) parts.push(`${sentinelBadCount} sensor${sentinelBadCount > 1 ? 's' : ''} reporting a fault`)
    return parts.length > 0 ? parts.join(', ') + ' — check connections before closing up' : undefined
  })()

  return (
    <div>
      <SectionLabel>Temperature Sensors</SectionLabel>

      <CheckRow
        icon={<Thermometer size={18} />}
        label={`Temperature Sensors${!isLoading && total > 0 ? ` (${total - badCount}/${total})` : ''}`}
        status={overallStatus}
        fixMessage={summaryFixMessage}
      />

      <div className="mt-1.5 flex flex-col gap-1.5">
        {isLoading
          ? [1, 2, 3].map(i => <Skeleton key={i} className="h-11 rounded-lg" />)
          : allNames.map((name, i) => {
              const reading = temperatures.find(t => t.name === name)
              const label   = `Temperature Sensor ${i + 1}`

              if (!reading) {
                return (
                  <SubRow
                    key={name}
                    label={label}
                    value={undefined}
                    ok={false}
                    fixMessage="Not connected — check the sensor plug"
                  />
                )
              }

              const sentinel = getTCSentinelStatus(reading.value)

              if (sentinel === 'open') {
                return (
                  <SubRow
                    key={name}
                    label={label}
                    value={undefined}
                    ok={false}
                    fixMessage="Sensor open — not attached correctly, check the connection"
                  />
                )
              }

              if (sentinel === 'shorted') {
                return (
                  <SubRow
                    key={name}
                    label={label}
                    value={undefined}
                    ok={false}
                    fixMessage="Sensor shorted — check for damaged wiring or connector"
                  />
                )
              }

              return (
                <SubRow
                  key={name}
                  label={label}
                  value={reading.value.toFixed(0)}
                  unit={reading.unit}
                  ok={true}
                />
              )
            })
        }
      </div>
    </div>
  )
}

// ─── Confetti ─────────────────────────────────────────────────────────────────

function useConfetti(trigger: boolean) {
  useEffect(() => {
    if (!trigger) return

    const canvas = document.createElement('canvas')
    canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999'
    document.body.appendChild(canvas)
    const ctx = canvas.getContext('2d')!
    canvas.width  = window.innerWidth
    canvas.height = window.innerHeight

    const pieces = Array.from({ length: 120 }, () => ({
      x:     Math.random() * canvas.width,
      y:     Math.random() * -canvas.height,
      w:     6 + Math.random() * 8,
      h:     10 + Math.random() * 6,
      r:     Math.random() * Math.PI * 2,
      dr:    (Math.random() - 0.5) * 0.15,
      dy:    3 + Math.random() * 4,
      dx:    (Math.random() - 0.5) * 2,
      color: ['#3b82f6', '#60a5fa', '#93c5fd', '#1d4ed8', '#bfdbfe'][Math.floor(Math.random() * 5)],
    }))

    let frame: number
    let alive = true

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      pieces.forEach(p => {
        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.r)
        ctx.fillStyle = p.color
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h)
        ctx.restore()
        p.y += p.dy
        p.x += p.dx
        p.r += p.dr
      })
      if (pieces.some(p => p.y < canvas.height) && alive) {
        frame = requestAnimationFrame(draw)
      } else {
        canvas.remove()
      }
    }

    frame = requestAnimationFrame(draw)
    return () => { alive = false; cancelAnimationFrame(frame); canvas.remove() }
  }, [trigger])
}

// ─── Readings section ─────────────────────────────────────────────────────────

function ReadingsSection({ installation }: { installation: InstallationData }) {
  const installationId = installation.id
  const phaseCount     = installation?.equipment_model?.phase_count
  const currentMetrics = phaseCount === 1
    ? [TSMetric.PHASE_A_CURRENT]
    : [TSMetric.ALL_PHASE_CURRENTS]

  // ── Grace period — 10s progress bar before showing any red states ──────────
  const [graceOver,     setGraceOver    ] = useState(false)
  const [progress,      setProgress     ] = useState(0)
  const [hasEverLoaded, setHasEverLoaded] = useState(false)

  useEffect(() => {
    const duration = 10_000
    const interval = 100
    const step     = (interval / duration) * 100

    const ticker = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(ticker)
          setGraceOver(true)
          return 100
        }
        return p + step
      })
    }, interval)

    return () => clearInterval(ticker)
  }, [])

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: temperatureResponse, isLoading: tempLoading } = useQuery({
    queryKey: ['temperatures', installationId, 'latest'],
    queryFn: async () => {
      const res = await apiClient.post<LineChartResponse>(
        ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY,
        {
          window_size: ONE_SECOND.value,
          start: LAST_1_MINUTE.value.from().toISOString(),
          stop:  LAST_1_MINUTE.value.to().toISOString(),
          installation_id: [installationId],
          timezone_for_bucketing: Intl.DateTimeFormat().resolvedOptions().timeZone,
          metrics: [TSMetric.ALL_TEMPERATURES],
          target_unit: 'degF',
        }
      )
      return res.data
    },
    refetchInterval: 2000,
    staleTime: 10_000,
  })

  const { data: powerResponse, isLoading: powerLoading } = useQuery({
    queryKey: ['power', installationId, phaseCount, 'latest'],
    queryFn: async () => {
      const res = await apiClient.post<LineChartResponse>(
        ApiPath.INSTALLATION_REFRIGERANT_CONTINUOUS_ACTIVE_POWER,
        {
          window_size: ONE_SECOND.value,
          start: LAST_1_MINUTE.value.from().toISOString(),
          stop:  LAST_1_MINUTE.value.to().toISOString(),
          installation_id: [installationId],
          timezone_for_bucketing: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
      )
      return res.data
    },
    enabled: phaseCount != null,
    refetchInterval: 2000,
    staleTime: 10_000,
  })

  const { data: phaseResponse, isLoading: phaseLoading } = useQuery({
    queryKey: ['phase-current', installationId, phaseCount, 'latest'],
    queryFn: async () => {
      const res = await apiClient.post<LineChartResponse>(
        ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY,
        {
          window_size: ONE_SECOND.value,
          start: LAST_1_MINUTE.value.from().toISOString(),
          stop:  LAST_1_MINUTE.value.to().toISOString(),
          installation_id: [installationId],
          timezone_for_bucketing: Intl.DateTimeFormat().resolvedOptions().timeZone,
          metrics: currentMetrics,
        }
      )
      return res.data
    },
    enabled: phaseCount != null,
    refetchInterval: 2000,
    staleTime: 10_000,
  })

  // ── Derived ────────────────────────────────────────────────────────────────

  const rawTemperatures = temperatureResponse ? extractLatestTemperatures(temperatureResponse) : []
  const allRawNames     = temperatureResponse?.series.map(s => s.name) ?? []
  const tcNameMap       = new Map(allRawNames.map((name, i) => [name, `Temperature Sensor ${i + 1}`]))
  const allTCNames      = allRawNames.map(name => tcNameMap.get(name)!)
  const temperatures    = rawTemperatures.map(t => ({ ...t, name: tcNameMap.get(t.name) ?? t.name }))

  const totalPower = powerResponse ? extractLatestValue(powerResponse, 0) : undefined

  const phases: PhaseReading[] = phaseResponse?.series
    ? phaseResponse.series.map((s, i) => ({
        label: s.name,
        value: extractLatestValue(phaseResponse, i),
      }))
    : []

  const hasCellular = temperatures.length > 0

  // Once we get any response back, we never go back to the loading banner
  useEffect(() => {
    if (temperatureResponse !== undefined) setHasEverLoaded(true)
  }, [temperatureResponse])

  const phaseConfigured = phaseCount != null

  // isInitialLoad is only true before the grace period ends AND before we've
  // ever gotten a response — after that we always show real status
  const isInitialLoad =
    !graceOver ||
    (!hasEverLoaded && (
      tempLoading ||
      (phaseConfigured && powerLoading) ||
      (phaseConfigured && phaseLoading)
    ))

  const allTCGood = allTCNames.length > 0 && allTCNames.every(name => {
    const reading = temperatures.find(t => t.name === name)
    return reading !== undefined && getTCSentinelStatus(reading.value) === 'ok'
  })
  const allPhasesGood = phases.length > 0 && phases.every(p => p.value !== undefined)
  const allGood =
    !isInitialLoad &&
    hasCellular &&
    allTCGood &&
    allPhasesGood

  useConfetti(allGood)

  return (
    <div className="space-y-5">

      {/* Grace period progress bar */}
      {!graceOver && (
        <div className="rounded-xl bg-gray-100 border-2 border-gray-200 px-4 py-3.5 space-y-2.5">
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-gray-400 flex-shrink-0" size={16} />
            <p className="text-xs text-gray-500 font-medium">Connecting to sensor…</p>
            <span className="ml-auto text-xs font-bold tabular-nums text-gray-400">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-600 rounded-full transition-all duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Waiting banner — grace over, never gotten a response yet, still loading */}
      {graceOver && !hasEverLoaded && isInitialLoad && (
        <div className="flex items-center gap-3 rounded-xl bg-gray-100 border-2 border-gray-200 px-4 py-3.5">
          <AlertTriangle className="text-gray-400 flex-shrink-0" size={18} />
          <p className="text-xs text-gray-500 font-medium leading-snug">
            Connecting to sensor — usually under a minute after power-on
          </p>
        </div>
      )}

      {/* All-clear banner */}
      {allGood && (
        <div className="flex items-center gap-3 rounded-xl bg-emerald-700 border-2 border-emerald-700 px-4 py-3.5">
          <CheckCircle2 className="text-white flex-shrink-0" size={20} />
          <div>
            <p className="text-sm font-bold text-white">All connected — safe to close up</p>
            <p className="text-xs text-emerald-200 mt-0.5">Every sensor and clamp is reading</p>
          </div>
        </div>
      )}

      <CellularCheck hasCellular={hasCellular} isLoading={isInitialLoad} />
      <PowerCheck totalPower={totalPower} phases={phases} isLoading={isInitialLoad} installation={installation} />
      <TemperatureCheck allNames={allTCNames} temperatures={temperatures} isLoading={isInitialLoad} />

    </div>
  )
}

// ─── Equipment Card ───────────────────────────────────────────────────────────

function EquipmentCard({ installation }: { installation: ReturnType<typeof useInstallation>['data'] }) {
  const equipmentModel = [installation.asset.make, installation.asset.model].filter(Boolean).join(' ')

  return (
    <Card className="p-5 border-2 border-gray-100">
      <div className="flex justify-center mb-3">
        <div className="inline-flex items-center gap-1.5 bg-emerald-700 rounded-full px-4 py-1.5">
          <CheckCircle2 size={12} className="text-white" />
          <span className="text-xs font-bold text-white tracking-wide">Sensor linked</span>
        </div>
      </div>

      <div className="text-center space-y-0.5">
        <p className="text-base font-bold text-gray-900 leading-tight">{installation.name}</p>
        {equipmentModel && (
          <p className="text-sm text-gray-500">{equipmentModel}</p>
        )}
        {installation.serial_number && (
          <p className="text-xs font-mono text-gray-400 pt-0.5">S/N {installation.serial_number}</p>
        )}
      </div>
    </Card>
  )
}

// ─── Page content ─────────────────────────────────────────────────────────────

function HealthCheckContent({ installationId }: { installationId: number }) {
  const { data: installation } = useInstallation(installationId)

  return (
    <div className="w-full space-y-4">
      <EquipmentCard installation={installation} />
      <ReadingsSection installation={installation} />
    </div>
  )
}

// ─── Root export ──────────────────────────────────────────────────────────────

export function HealthStep() {
  const organizationId = useSelectedOrganizationId()
  const search         = useStepSearch('health')
  const installationId = search.installationId
  const navigate       = useNavigate()
  const { data: installation } = useInstallation(installationId)

  const handleViewInCopilot = () => {
    navigate({
      to: '/organization/$organizationId/locations/$locationId/equipment/$equipmentId',
      params: {
        organizationId,
        locationId: search.locationId,
        equipmentId: installation.equipment,
      },
    })
  }

  return (
    <StepFrame
      header={<StepHeader title="Installation complete!" />}
      main={<HealthCheckContent installationId={installationId} />}
      footer={
        <StepFooter
          actions={[
            <Button
              key="view"
              onClick={handleViewInCopilot}
              className="w-full"
              variant="thalo"
              size="lg"
            >
              View in Copilot
            </Button>,
          ]}
        />
      }
    />
  )
}
